import { call, getContext, put, select, takeEvery } from 'redux-saga/effects';
import startCase from 'lodash.startcase';
import { completeInteraction, FETCH_USER_PROPERTIES } from 'ext/lib/track';
import { INITIALIZE } from 'ext/root/root-actions';
import {
  resolve,
  reject,
  registerUserPropertyFailed,
  REGISTER,
} from './actions';
import { selectUserProperties } from './reducer';

/*
 * Format user properties based on existing logic from `crx-ui`
 *
 * The label record is the source of the display name when one is set — the
 * account may have labelled an attribute in Studio. `startCase` of the key is
 * only the fallback.
 *
 * @param {object<string, object>} response - User properties response
 * @param {object<string, object>} labelsByName - Label records, keyed by name
 * @return {UserProperty[]} Parsed user properties
 */
const parse = (response, labelsByName) => {
  // Hidden on anything falsy, not just an explicit `false` — a record that
  // omits the field was hidden before this read grew a union, and widening
  // that would expose attributes the account chose to hide.
  const isHidden = name =>
    Object.hasOwn(labelsByName, name) && !labelsByName[name].showInUI;
  const toName = name =>
    (Object.hasOwn(labelsByName, name) && labelsByName[name].label) ||
    startCase(name);

  const observed = Object.entries(response)
    .filter(([property]) => !isHidden(property))
    .map(([property, values]) => ({
      name: toName(property),
      value: property,
      options: Object.keys(values).map(value => ({ name: value })),
    }));

  // Attributes registered but never sent — including any authored in a builder
  // — exist only as a label record, so they'd otherwise be invisible.
  const unobserved = Object.keys(labelsByName)
    .filter(name => !isHidden(name) && !Object.hasOwn(response, name))
    .map(name => ({ name: toName(name), value: name, options: [] }));

  return [...observed, ...unobserved];
};

export function* fetchUserProperties() {
  try {
    const { getUserProperties, getUserPropertiesLabels } =
      yield getContext('api');

    let labelsByName = {};

    if (getUserPropertiesLabels) {
      const userPropertiesLabels = yield call(getUserPropertiesLabels);
      labelsByName = Object.fromEntries(
        userPropertiesLabels.map(label => [label.name, label])
      );
    }

    const response = yield call(getUserProperties);
    yield put(resolve(parse(response, labelsByName)));
    yield put(completeInteraction(FETCH_USER_PROPERTIES));
  } catch (error) {
    yield put(reject(error));
  }
}

/*
 * Register an attribute authored in a builder so every experience — and every
 * other user on the account — can use it. The account only learns about an
 * attribute once data has been sent for it, so without this the attribute is
 * invisible outside the experience that set it.
 */
export function* registerUserProperty({ payload: { value, name } }) {
  const existing = (yield select(selectUserProperties)) ?? [];

  // Never write over an attribute the account already has — the upsert would
  // replace a label someone set in Studio. The picker hides some attributes
  // (timestamp-looking, Appcues' own), so it can offer to "create" one that
  // exists; this is the check that has the whole list.
  const known = existing.some(
    property => property.value.toLowerCase() === value.toLowerCase()
  );

  if (known) {
    return;
  }

  // Show it immediately; a failed write shouldn't block authoring, and the
  // experience it was set in carries it either way.
  yield put(resolve([...existing, { name, value, options: [] }]));

  try {
    const { upsertUserPropertyLabel } = yield getContext('api');
    yield call(upsertUserPropertyLabel, value, name);
  } catch (error) {
    yield put(registerUserPropertyFailed(error));
  }
}

export default function* userPropertiesSaga() {
  yield takeEvery(INITIALIZE, fetchUserProperties);
  yield takeEvery(REGISTER, registerUserProperty);
}
